import { LocationPreviewV4OutDTO, SupplierNotificationToExpeditionOutDTO } from '@api/ringil-api.v2.tsx';
import { ExpeditionDetailForPrefill } from '@app/pages/expedition/create/mappers/detailToForm.ts';

/**
 * Adapts a supplier notification into the shipment-detail shape the prefill mapper consumes, so
 * "create shipment from notification" goes through exactly the same path as "copy shipment".
 *
 * A notification records what is loaded where but not where any of it is unloaded, so everything
 * still loaded after the last-but-one stop is unloaded at the final stop.
 */
export const mapSupplierNotificationToExpeditionDetail = (notification: SupplierNotificationToExpeditionOutDTO): ExpeditionDetailForPrefill => {
  const locations = (notification.locations || []).map(loc => ({
    id: String(loc.id),
    name: loc.name,
    country: loc.country,
    postalCode: loc.postalCode,
    city: loc.city,
    streetNr: loc.streetNr,
    contactName: loc.contactName,
    contactPhone: loc.contactPhone,
    contactEmail: loc.contactEmail,
    // RD-2916: SN referral code travels as the location reference (no longer part of the carrier note)
    loadingReference: loc.pickupCode,
    notifySelectedPerson: loc.enabledEmailNotificationForContactPerson,
    isPartnerLocation: !!loc.partnerLocationId,
    isExternal: false,
    deliverySlot: loc.timeslots,
    timezoneId: loc.timezoneId,
    unloadedOrders: [] as any[],
    loadedOrders: (loc.loadedOrders || []).map(order => ({
      ...order,
      orderValue: {
        price: order.orderValue?.amount ?? 0,
        currency: order.orderValue?.currencyCode ?? '',
      },
      items: (order.items || []).map((item, idx) => ({
        id: String(idx),
        name: item.name,
        width: item.width,
        height: item.height,
        length: item.length,
        weight: item.weight,
        amount: item.amount,
        quantityUnit: item.quantityUnit,
        stackable: item.stackable,
        weightMode: item.weightMode,
        cargoTemplate: item.cargoTemplate ?? null,
        itemNote: item.itemNote,
      })),
    })),
    selectedPerson: {
      contactName: loc.contactName,
      contactPhone: loc.contactPhone,
      contactEmail: loc.contactEmail,
      automaticPrefillToNotify: loc.enabledEmailNotificationForContactPerson,
    },
    additionalContacts: [
      ...(loc.contactEmail
        ? [
          {
            contactName: loc.contactName,
            contactPhone: loc.contactPhone,
            contactEmail: loc.contactEmail,
            automaticPrefillToNotify: loc.enabledEmailNotificationForContactPerson,
          },
        ]
        : []),
      ...(loc.contactPersons || []),
    ].filter((contact, index, self) => contact.contactEmail && self.findIndex(c => c.contactEmail === contact.contactEmail) === index),
    companyLocationId: loc.partnerLocationId,
    requirementCodes: [] as string[],
  }));

  const allOrderIds = locations
    .slice(0, locations.length - 1)
    .map(loc => loc.loadedOrders || [])
    .flat()
    .map(order => order.id);

  const alreadyUnloadedIds = locations
    .slice(0, locations.length - 1)
    .map(loc => loc.unloadedOrders || [])
    .flat()
    .map(order => order.id);

  const notUnloadedOrderIds = allOrderIds.filter(id => !alreadyUnloadedIds.includes(id));

  if (locations.length > 0) {
    const lastLocation = locations[locations.length - 1];
    lastLocation.unloadedOrders = [
      ...(lastLocation.unloadedOrders || []),
      ...notUnloadedOrderIds.map(id => {
        const order = allOrderIds
          .map(orderId =>
            locations
              .slice(0, locations.length - 1)
              .map(loc => loc.loadedOrders || [])
              .flat()
              .find(o => o.id === orderId),
          )
          .find(o => o && o.id === id);
        return order || { id };
      }),
    ];
  }

  return {
    id: notification.supplierNotificationFromId,
    name: '',
    expeditionType: notification.expeditionType,
    locations: locations as unknown as LocationPreviewV4OutDTO[],
    internalInfo: {
      folders: [],
      responsiblePerson: null,
      internalNote: notification.internalNote,
      codeListNotificationEmails: [],
    },
    cargoTypeCode: notification.cargoType?.code,
    adrWeight: notification.adrWeight,
    requirementCodes: notification.requirements?.map(r => r.code),
    transportationType: notification.transportationType,
    loadingTypes: [],
    carrierNote: notification.note,
    labels: [],
    distributions: [],
    fromSupplierNotification: true,
    allowPartialDelivery: false,
    supplierNotificationLink: {
      supplierNotificationId: notification.supplierNotificationFromId,
      supplierNotificationApplicationId: notification.applicationId,
    },
  };
};